import { useState, useEffect, useRef } from "react";
import { Lock } from "lucide-react";
import LessonDrawer from "@/components/workspace/LessonDrawer";
import api from "@/utils/axios-utils";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import {
  useSpaceFormOptions,
  MAX_LESSONS_PER_SPACE,
} from "../hooks/useSpaceFormOptions";
import { EditSpaceModalSkeleton } from "./SpaceLoadingSkeletons";
import type { SpaceStatus } from "../store/useSpaceStore";

type SpaceEditData = {
  spaceId: string;
  spaceName: string;
  description: string | null;
  lessonContext: string | null;
  status: SpaceStatus;
  lessonImported: boolean;
  classroomIds: number[];
  lessonIds: number[];
};

type SavedSpace = {
  spaceId: string;
  spaceName: string;
  description: string | null;
  lessonContext: string | null;
  classroomIds: number[];
  lessonIds: number[];
};

export function EditSpaceModal({
  spaceId,
  onClose,
  onSaved,
}: {
  spaceId: string;
  onClose: () => void;
  onSaved: (updated: SavedSpace) => void;
}) {
  const { showNotification } = useNotificationStore();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const inFlight = useRef(false);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState<SpaceStatus>("draft");
  const [lessonImported, setLessonImported] = useState(false);

  const [spaceName, setSpaceName] = useState("");
  const [description, setDescription] = useState("");
  const [lessonContext, setLessonContext] = useState("");
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<number[]>(
    [],
  );
  const [initialClassroomIds, setInitialClassroomIds] = useState<number[]>([]);
  const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([]);

  const [initialSpaceName, setInitialSpaceName] = useState("");
  const [initialDescription, setInitialDescription] = useState("");
  const [initialLessonContext, setInitialLessonContext] = useState("");
  const [initialLessonIds, setInitialLessonIds] = useState<number[]>([]);

  const { classrooms, classroomsLoading, lessons, lessonsLoading } =
    useSpaceFormOptions();

  const isDraft = status === "draft";
  const isActive = status === "open" || status === "paused";
  const canEditLessons = isDraft && !lessonImported;
  const canEditClassrooms = isDraft || isActive;
  const canEditOtherFields = isDraft;

  const hasChanges =
    spaceName.trim() !== initialSpaceName.trim() ||
    description.trim() !== initialDescription.trim() ||
    lessonContext.trim() !== initialLessonContext.trim() ||
    selectedClassroomIds.slice().sort().join(",") !==
      initialClassroomIds.slice().sort().join(",") ||
    selectedLessonIds.slice().sort().join(",") !==
      initialLessonIds.slice().sort().join(",");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    api
      .get<SpaceEditData>(`/spaces/${spaceId}/edit-data`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        setStatus(d.status as SpaceStatus);
        setLessonImported(d.lessonImported);
        setSpaceName(d.spaceName);
        setDescription(d.description ?? "");
        setLessonContext(d.lessonContext ?? "");
        setSelectedClassroomIds(d.classroomIds);
        setInitialClassroomIds(d.classroomIds);
        setSelectedLessonIds(d.lessonIds);
        setInitialSpaceName(d.spaceName);
        setInitialDescription(d.description ?? "");
        setInitialLessonContext(d.lessonContext ?? "");
        setInitialLessonIds(d.lessonIds);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [spaceId, retry]);

  async function handleSave() {
    if (inFlight.current || loading || loadError) return;
    setSaveError(null);
    if (!spaceName.trim()) {
      showNotification("Space name is required", NotificationType.WARNING);
      return;
    }
    if (canEditClassrooms && selectedClassroomIds.length === 0) {
      showNotification(
        "At least one classroom is required",
        NotificationType.WARNING,
      );
      return;
    }
    setIsSaving(true);
    inFlight.current = true;
    try {
      const body: Record<string, unknown> = {
        spaceName: spaceName.trim(),
      };
      if (isDraft) {
        body.description = description.trim() || null;
        body.lessonContext = lessonContext.trim() || null;
        body.lessonIds = selectedLessonIds;
      }
      if (canEditClassrooms) {
        body.classroomIds = selectedClassroomIds;
      }

      await api.patch(`/spaces/${spaceId}/metadata`, body);

      showNotification("Space updated", NotificationType.SUCCESS);
      onSaved({
        spaceId,
        spaceName: spaceName.trim(),
        description: description.trim() || null,
        lessonContext: lessonContext.trim() || null,
        classroomIds: selectedClassroomIds,
        lessonIds: selectedLessonIds,
      });
    } catch {
      setSaveError(
        "Could not save changes. Your entries are preserved; please try again.",
      );
      showNotification("Failed to save changes", NotificationType.WARNING);
    } finally {
      inFlight.current = false;
      setIsSaving(false);
    }
  }

  const inputClass =
    "w-full rounded-[6px] border border-gray-200 px-4 py-3 text-sm font-mulish text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent bg-white transition disabled:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed";

  function LockedHint({ text }: { text: string }) {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-400 font-mulish">
        <Lock className="w-3 h-3" />
        {text}
      </span>
    );
  }

  return (
    <LessonDrawer
      title="Edit Space"
      eyebrow="Space settings"
      closeLabel="Close Space settings"
      className="et-compact-dialog et-edit-space"
      onClose={() => {
        if (!inFlight.current) onClose();
      }}
    >
      <h2>Edit Space</h2>

      {loading ? (
        <EditSpaceModalSkeleton />
      ) : loadError ? (
        <div role="alert" className="et-group-error">
          <p>Could not load Space settings.</p>
          <button onClick={() => setRetry((n) => n + 1)}>Try again</button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="flex flex-col gap-5"
        >
          {/* Space name — always editable for draft/active */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="edit-space-name"
              className="text-sm font-bold text-gray-800"
            >
              Space name
            </label>
            <input
              id="edit-space-name"
              type="text"
              value={spaceName}
              onChange={(e) => setSpaceName(e.target.value)}
              maxLength={100}
              className={inputClass}
            />
          </div>

          {/* Classrooms */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="edit-space-classrooms"
              className="text-sm font-bold text-gray-800"
            >
              Classrooms
            </label>
            {canEditClassrooms ? (
              <>
                <MultiSelectDropdown
                  id="edit-space-classrooms"
                  options={classrooms}
                  selected={selectedClassroomIds}
                  onChange={setSelectedClassroomIds}
                  placeholder="Select classrooms…"
                  loading={classroomsLoading}
                  disabled={false}
                  lockedIds={isActive ? initialClassroomIds : undefined}
                />
                {isActive && (
                  <LockedHint text="You can add classrooms but not remove them while the space is active." />
                )}
              </>
            ) : (
              <>
                <MultiSelectDropdown
                  id="edit-space-classrooms"
                  options={classrooms}
                  selected={selectedClassroomIds}
                  onChange={() => {}}
                  placeholder="Select classrooms…"
                  loading={classroomsLoading}
                  disabled
                  disabledHint="Classrooms cannot be changed for closed spaces."
                />
                <LockedHint text="Classrooms cannot be changed for closed spaces." />
              </>
            )}
          </div>

          {/* Lessons */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="edit-space-lessons"
              className="text-sm font-bold text-gray-800"
            >
              Lessons{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <MultiSelectDropdown
              id="edit-space-lessons"
              options={lessons}
              selected={selectedLessonIds}
              onChange={setSelectedLessonIds}
              placeholder="No lesson — open whiteboard"
              loading={lessonsLoading}
              maxCount={MAX_LESSONS_PER_SPACE}
              disabled={!canEditLessons}
              disabledHint={
                lessonImported
                  ? "Lessons cannot be changed after slides have been imported."
                  : "Lessons cannot be changed once the space has been launched."
              }
            />
            {!canEditLessons && (
              <LockedHint
                text={
                  lessonImported
                    ? "Slides already imported — lessons are locked."
                    : "Lessons are locked once the space is launched."
                }
              />
            )}
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="edit-space-description"
              className="text-sm font-bold text-gray-800"
            >
              Description{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="edit-space-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              disabled={!canEditOtherFields}
              placeholder="e.g. Warm-up on equivalent fractions for Period 2"
              className={inputClass}
            />
            {!canEditOtherFields && (
              <LockedHint text="Description can only be edited for draft spaces." />
            )}
          </div>

          {/* AI instructions */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="edit-space-instructions"
              className="text-sm font-bold text-gray-800"
            >
              Instructions to AI{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="edit-space-instructions"
              value={lessonContext}
              onChange={(e) => setLessonContext(e.target.value)}
              rows={3}
              maxLength={2000}
              disabled={!canEditOtherFields}
              placeholder="e.g. We've been working on equivalent fractions."
              className={`${inputClass} resize-y`}
            />
            <div className="flex justify-between items-center">
              {!canEditOtherFields ? (
                <LockedHint text="AI instructions can only be edited for draft spaces." />
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400">
                {lessonContext.length}/2000
              </span>
            </div>
          </div>

          {/* Actions */}
          {saveError && (
            <p role="alert" className="et-group-error">
              {saveError}
            </p>
          )}
          <div className="flex items-center justify-end gap-3 pt-5 border-t border-gray-100">
            <button
              type="button"
              disabled={isSaving}
              onClick={onClose}
              className="text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !hasChanges}
              className="h-9 px-5 rounded-[6px] bg-[#28543D] hover:bg-[#204833] text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      )}
    </LessonDrawer>
  );
}
